import type { ReactElement } from 'react';
import { LanguageSwitcher, SegmentedControl, cn, useTheme } from '@xdocs/ui';
import type { ThemePreference } from '@xdocs/ui';
import { useLocaleController } from '../../i18n/LocaleController.js';
import { useSettingsT } from './useSettingsT.js';
import styles from './AppearanceSection.module.css';

/**
 * F10 Appearance section (theme + language). Deliberately thin: theme and
 * locale are already owned end-to-end by `ThemeController`/`LocaleController`
 * (localStorage source of truth) + `useSyncSettingsToServer` (best-effort
 * mirrors to `users.theme`/`users.locale` via the already-shipped
 * `settings.setTheme`/`settings.setLocale` ops, both mounted in
 * `AppShellLayout`, out of this task's ownership) — this section reuses the
 * SAME `useTheme()`/`useLocaleController()` state rather than re-deriving a
 * second source of truth or re-implementing persistence.
 */
export function AppearanceSection(): ReactElement {
  const { t } = useSettingsT();
  const { theme, setTheme } = useTheme();
  const { setLocale } = useLocaleController();

  const options: { value: ThemePreference; label: string }[] = [
    { value: 'system', label: t('settings:appearance.theme.system') },
    { value: 'light', label: t('settings:appearance.theme.light') },
    { value: 'dark', label: t('settings:appearance.theme.dark') },
  ];

  return (
    <section className={cn(styles['card'])}>
      <h2 className={cn(styles['title'])}>{t('settings:appearance.title')}</h2>

      <div className={cn(styles['row'])}>
        <span className={cn(styles['label'])}>{t('settings:appearance.theme.label')}</span>
        <SegmentedControl label={t('settings:appearance.theme.label')} options={options} value={theme} onChange={setTheme} />
      </div>

      <div className={cn(styles['row'])}>
        <span className={cn(styles['label'])}>{t('settings:appearance.language.label')}</span>
        <LanguageSwitcher onLocaleChange={setLocale} />
      </div>
    </section>
  );
}
